import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { usePracticeProfile, useUpdatePracticeProfile } from '@/hooks/usePracticeProfile'
import { useProviderAuth } from '@/hooks/auth'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import LoadingPage from '@/components/shared/LoadingPage'
import ErrorState from '@/components/shared/ErrorState'

const practiceProfileSchema = z.object({
  name: z.string().min(1, 'Practice name is required'),
  address_street: z.string().min(1, 'Street address is required'),
  address_city: z.string().min(1, 'City is required'),
  address_state: z.string().min(1, 'State is required'),
  address_zip: z.string().min(1, 'ZIP code is required'),
  phone: z.string().min(1, 'Phone is required'),
  fax: z.string().optional(),
  tax_id: z.string().min(1, 'Tax ID is required'),
  group_npi: z.string().min(1, 'Group NPI is required'),
})

type PracticeProfileForm = z.infer<typeof practiceProfileSchema>

function PracticeProfileContent({ readonly }: { readonly?: boolean }) {
  const { data: profile, isLoading, error, refetch } = usePracticeProfile()
  const updateProfile = useUpdatePracticeProfile()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<PracticeProfileForm>({
    resolver: zodResolver(practiceProfileSchema),
    defaultValues: {
      name: '',
      address_street: '',
      address_city: '',
      address_state: '',
      address_zip: '',
      phone: '',
      fax: '',
      tax_id: '',
      group_npi: '',
    },
  })

  useEffect(() => {
    if (profile) {
      reset({
        name: profile.name,
        address_street: profile.address.street,
        address_city: profile.address.city,
        address_state: profile.address.state,
        address_zip: profile.address.zip,
        phone: profile.phone,
        fax: profile.fax,
        tax_id: profile.tax_id,
        group_npi: profile.group_npi,
      })
    }
  }, [profile, reset])

  const onSubmit = async (data: PracticeProfileForm) => {
    try {
      await updateProfile.mutateAsync({
        name: data.name,
        address: {
          street: data.address_street,
          city: data.address_city,
          state: data.address_state,
          zip: data.address_zip,
        },
        phone: data.phone,
        fax: data.fax ?? '',
        tax_id: data.tax_id,
        group_npi: data.group_npi,
        integrations: profile?.integrations ?? {},
      })
      toast.success('Practice profile saved')
    } catch {
      toast.error('Failed to save practice profile')
    }
  }

  if (isLoading) {
    return <LoadingPage variant="form" />
  }

  if (error) {
    return <ErrorState message="Failed to load practice profile." onRetry={() => refetch()} />
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-slate-900">Practice Profile</h1>

      <form onSubmit={readonly ? (e) => e.preventDefault() : handleSubmit(onSubmit)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Practice Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Practice Name</Label>
              <Input id="name" {...register('name')} disabled={readonly} />
              {errors.name && <p className="text-sm text-red-600">{errors.name.message}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="address_street">Street Address</Label>
              <Input id="address_street" {...register('address_street')} disabled={readonly} />
              {errors.address_street && <p className="text-sm text-red-600">{errors.address_street.message}</p>}
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="address_city">City</Label>
                <Input id="address_city" {...register('address_city')} disabled={readonly} />
                {errors.address_city && <p className="text-sm text-red-600">{errors.address_city.message}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="address_state">State</Label>
                <Input id="address_state" {...register('address_state')} disabled={readonly} />
                {errors.address_state && <p className="text-sm text-red-600">{errors.address_state.message}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="address_zip">ZIP</Label>
                <Input id="address_zip" {...register('address_zip')} disabled={readonly} />
                {errors.address_zip && <p className="text-sm text-red-600">{errors.address_zip.message}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input id="phone" {...register('phone')} disabled={readonly} />
                {errors.phone && <p className="text-sm text-red-600">{errors.phone.message}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="fax">Fax</Label>
                <Input id="fax" {...register('fax')} disabled={readonly} />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="tax_id">Tax ID</Label>
                <Input id="tax_id" {...register('tax_id')} disabled={readonly} />
                {errors.tax_id && <p className="text-sm text-red-600">{errors.tax_id.message}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="group_npi">Group NPI</Label>
                <Input id="group_npi" {...register('group_npi')} disabled={readonly} />
                {errors.group_npi && <p className="text-sm text-red-600">{errors.group_npi.message}</p>}
              </div>
            </div>
          </CardContent>
        </Card>

        {!readonly && (
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save Practice Profile'}
          </Button>
        )}
      </form>
    </div>
  )
}

export default function PracticeProfilePage() {
  const { provider } = useProviderAuth()
  const isAdmin = provider?.is_admin ?? false

  return <PracticeProfileContent readonly={!isAdmin} />
}
